import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Handshake, Lock, Trash2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg, fmtDate, fmtDateTime, pretty, isStaffRole, ROLE_LABEL, PRIORITY_CLS, DOC_CATEGORIES } from "@/lib/api";

const Section = ({ title, children, testId }) => (
  <div className="space-y-3 rounded-lg border p-4" data-testid={testId}>
    <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</h4>
    {children}
  </div>
);

export function TaskMeta({ task }) {
  const { isStaff } = useAuth();
  const va = task.handling === "va_assist" || task.responsibility_status === "pending_va";
  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-4 text-sm" data-testid="task-meta">
      <div><div className="text-[11px] uppercase text-muted-foreground">Handled by</div>
        <span data-testid="task-handling-badge" className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${va ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700"}`}>{va ? "V.A assistance" : "Client internal"}</span></div>
      <div><div className="text-[11px] uppercase text-muted-foreground">Assignee</div><div className="font-medium" data-testid="task-assignee">{task.assignee_name ? `${task.assignee_name} (${ROLE_LABEL[task.assignee_role]})` : "Unassigned"}</div></div>
      <div><div className="text-[11px] uppercase text-muted-foreground">Priority</div><div className={`capitalize ${PRIORITY_CLS[task.priority || "normal"]}`} data-testid="task-priority">{task.priority || "normal"}</div></div>
      {isStaff && <div><div className="text-[11px] uppercase text-muted-foreground">Internal deadline</div><div data-testid="task-internal-due">{fmtDate(task.internal_due_date)}</div></div>}
      {task.source === "document_expiry" && <div className="col-span-2 text-xs">Generated from document expiry: <strong>{task.source_document_title}</strong> (expires {fmtDate(task.period_end)})</div>}
      {task.assistance && <div className="col-span-2 flex items-center gap-2 text-xs">Assistance {task.assistance.ref}: <StatusBadge kind="assist" status={task.assistance.status} testId="task-assist-status" />{task.assistance.decision_reason && <span className="text-muted-foreground">{task.assistance.decision_reason}</span>}</div>}
      {task.status === "returned" && task.last_return && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800" data-testid="task-return-reason"><strong>Returned by {task.last_return.by}:</strong> {task.last_return.reason}</div>}
    </div>
  );
}

export function ApprovalBox({ task, onChanged }) {
  const { user } = useAuth();
  const approve = () => api.post(`/tasks/${task.id}/approve`).then(() => { toast.success("Task approved and published to client"); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  if (!can(user, "tasks_generate")) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="approval-box">
      <span className="text-amber-900">Generated automatically. Review/correct the due date, then approve to make it visible to the client.</span>
      <Button size="sm" onClick={approve} data-testid="task-approve-btn">Approve task</Button>
    </div>
  );
}

export function AssignBox({ task, onChanged }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.status === "active" && (user.role === "admin" ? (isStaffRole(u.role) || u.tenant_id === task.tenant_id) : u.tenant_id === task.tenant_id))));
  }, [task.tenant_id, user.role]);
  const assign = async () => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/assign`, { assignee_id: sel, note: note || null });
      const msg = { sent: "Assigned · email sent", failed: "Assigned · email delivery FAILED (logged)", not_configured: "Assigned · email not configured" }[data.email_status];
      data.email_status === "sent" ? toast.success(msg) : toast.warning(msg);
      setSel(""); setNote(""); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={task.assignee_id ? "Reassign task" : "Assign task"} testId="assign-box">
      <Select value={sel} onValueChange={setSel}>
        <SelectTrigger data-testid="assignee-select"><SelectValue placeholder="Choose a person" /></SelectTrigger>
        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABEL[u.role]}</SelectItem>)}</SelectContent>
      </Select>
      <Input placeholder="Assignment details (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="assign-note-input" />
      <Button size="sm" disabled={!sel} onClick={assign} data-testid="assign-submit-btn"><UserCheck className="mr-1 h-4 w-4" /> Assign</Button>
    </Section>
  );
}

export function StaffMetaEdit({ task, onChanged }) {
  const { user } = useAuth();
  const [f, setF] = useState({ due_date: task.due_date, internal_due_date: task.internal_due_date || "", priority: task.priority || "normal" });
  const pri = can(user, "tasks_priority");
  const save = async () => {
    const body = { due_date: f.due_date };
    if (pri) Object.assign(body, { priority: f.priority, internal_due_date: f.internal_due_date || null });
    try { await api.patch(`/tasks/${task.id}`, body); toast.success("Task updated"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!can(user, "tasks_edit")) return null;
  return (
    <Section title="Deadline & priority" testId="task-meta-edit">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Due date"><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input" /></Field>
        {pri && <Field label="Internal deadline"><Input type="date" value={f.internal_due_date} onChange={(e) => setF({ ...f, internal_due_date: e.target.value })} data-testid="task-internal-due-input" /></Field>}
        {pri && (
          <Field label="Priority">
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{pretty(p)}</SelectItem>)}</SelectContent></Select>
          </Field>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={save} data-testid="task-meta-save-btn">Save</Button>
    </Section>
  );
}

export function AssistButton({ task, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ service: "", instructions: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const ids = [];
      if (file) {
        const fd = new FormData();
        fd.append("file", file); fd.append("category", "evidence"); fd.append("title", `Assistance attachment – ${file.name}`); fd.append("task_id", task.id);
        ids.push((await api.post("/documents", fd)).data.id);
      }
      await api.post(`/tasks/${task.id}/assistance`, { ...f, attachment_ids: ids });
      toast.success("Request sent to the V.A assistance queue"); setOpen(false); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setOpen(true)} data-testid="request-va-assistance-btn"><Handshake className="mr-2 h-4 w-4" /> Request V.A Assistance</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assistance-dialog">
          <DialogHeader><DialogTitle>Request V.A assistance</DialogTitle><DialogDescription>{task.title} · {task.period_key} · due {fmtDate(task.due_date)}</DialogDescription></DialogHeader>
          <Field label="Service required"><Input placeholder="e.g. Prepare and file the return" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="assist-service-input" /></Field>
          <Field label="Instructions / comments"><Textarea rows={4} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="assist-instructions-input" /></Field>
          <Field label="Attachment (optional)"><Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="assist-file-input" /></Field>
          <DialogFooter><Button disabled={busy || f.service.length < 2 || f.instructions.length < 5} onClick={submit} data-testid="assist-submit-btn">{busy ? "Sending…" : "Send request"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CommentsBox({ task, onChanged }) {
  const { isStaff } = useAuth();
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(isStaff);
  const post = async () => {
    try { await api.post(`/tasks/${task.id}/comments`, { body, internal: isStaff && internal }); setBody(""); setInternal(isStaff); toast.success("Remark added"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = [...(task.comments || [])].sort((a, z) => (a.at || "").localeCompare(z.at || ""));
  return (
    <Section title={`Remarks & comments (${list.length})`} testId="task-comments">
      {list.map((c) => (
        <div key={c.id} className={`rounded-md p-3 text-sm ${c.internal ? "border border-dashed border-amber-300 bg-amber-50 dark:bg-amber-950/30" : isStaffRole(c.by_role) ? "bg-blue-50 dark:bg-blue-950/30" : "bg-muted"}`} data-testid={`task-comment-${c.id}`}>
          <div className="mb-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">{c.internal && <Lock className="h-3 w-3" />}{c.by_name} ({ROLE_LABEL[c.by_role]}) · {fmtDateTime(c.at)}{c.stage && ` · ${c.stage}`}
            <span className="ml-auto font-medium" data-testid="task-comment-audience">{c.internal ? "Internal · V.A only" : "Visible to client"}</span></div>{c.body}
        </div>
      ))}
      <Textarea placeholder={internal && isStaff ? "Internal remark: progress, delays, or help you need from a supervisor" : "Comment the client can read"} value={body} onChange={(e) => setBody(e.target.value)} data-testid="task-comment-input" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {isStaff ? <label className="flex items-center gap-2 text-xs"><Switch checked={internal} onCheckedChange={setInternal} data-testid="task-comment-internal-switch" /> {internal ? "Internal V.A remark (hidden from the client)" : "Client-facing comment (the client can read it)"}</label> : <span className="text-xs text-muted-foreground">Visible to your company admins and V.A.</span>}
        <Button size="sm" disabled={!body.trim()} onClick={post} data-testid="task-comment-submit">{internal && isStaff ? "Add remark" : "Post comment"}</Button>
      </div>
    </Section>
  );
}

export function PromoteButton({ doc, onDone }) {
  const { user } = useAuth();
  const [cat, setCat] = useState("");
  const go = () => api.post(`/documents/${doc.id}/promote`, { decision: "approve", category: cat }).then(() => { toast.success("Placed in company vault"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  if (!can(user, "docs_promote")) return null;
  return (
    <div className="flex items-center gap-1">
      <Select value={cat} onValueChange={setCat}>
        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`promote-category-${doc.id}`}><SelectValue placeholder="Vault cat." /></SelectTrigger>
        <SelectContent>{DOC_CATEGORIES.filter((c) => c !== "evidence").map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!cat} onClick={go} data-testid={`promote-btn-${doc.id}`}>Add to vault</Button>
    </div>
  );
}

export function AdminActions({ task, onDone }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const pristine = task.status === "not_started" && (task.history || []).length <= 1 && !(task.comments || []).length && !task.documents.length && !task.assignee_id && !task.assistance_request_id;
  const run = async () => {
    try {
      await api.post(`/tasks/${task.id}/${mode}`, { reason });
      toast.success(mode === "delete" ? "Task deleted (recorded in audit log)" : "Task cancelled and archived");
      setMode(null); setReason(""); onDone(mode);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Admin actions" testId="admin-task-actions">
      <div className="flex flex-wrap gap-2">
        {can(user, "tasks_cancel") && <Button size="sm" variant="outline" onClick={() => setMode("cancel")} data-testid="task-cancel-btn"><Archive className="mr-1 h-4 w-4" /> Cancel / archive</Button>}
        {can(user, "tasks_delete") && <Button size="sm" variant="outline" className="text-red-600" disabled={!pristine} onClick={() => setMode("delete")} data-testid="task-delete-btn"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>}
      </div>
      {!pristine && <p className="text-xs text-muted-foreground">Delete is only available for untouched tasks. Tasks with activity or evidence must be cancelled to preserve history.</p>}
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent data-testid="task-admin-confirm-dialog">
          <DialogHeader><DialogTitle>{mode === "delete" ? "Delete task permanently?" : "Cancel and archive task?"}</DialogTitle>
            <DialogDescription>{task.title} · {task.company_name} · {task.period_key}. {mode === "delete" ? "It will not be regenerated. A snapshot is kept in the audit log." : "History is preserved; the task is excluded from progress figures."}</DialogDescription></DialogHeader>
          <Field label="Reason (required, recorded in audit log)"><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="task-admin-reason-input" /></Field>
          <DialogFooter><Button variant={mode === "delete" ? "destructive" : "default"} disabled={reason.trim().length < 5} onClick={run} data-testid="task-admin-confirm-btn">Confirm {mode}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
